import { useAuth0 } from "@auth0/auth0-react";
import {
  Delete,
  Download,
  Fullscreen,
  PictureAsPdf,
  UploadFile,
  WarningAmber,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  type Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { USE_AUTH } from "../../../api/apiConfig";
import { toSourceUrls } from "../../../api/sources";
import { formatDateTime } from "../../../utils/dateUtils";
import Source from "../../PDFViewer/Source";

type DocumentList = [[string, Array<{ fileId: string; path: string }>]];

// Computed up front (as the pdfWidth state's initial value) rather than only in a
// post-mount effect: rendering a Page at a placeholder width and then immediately
// re-rendering it at the real width cancels react-pdf's in-flight canvas render,
// which can leave the page blurry or solid black.
function calculatePdfWidth(theme: Theme): number {
  const viewportPercentage = 0.4;
  const themePadding = theme.spacing(8);
  return window.innerWidth * viewportPercentage - parseInt(themePadding, 10);
}

interface PDFSidebarProps {
  fileId: string | null;
  filePath: string | null;
  bennyLastAttemptedAt?: string | null;
  bennyRetrievalStatus?: string | null;
  bennyRetrievalReason?: string | null;
  onUploadPdf?: (file: File) => Promise<void>;
  onRemovePdf?: () => Promise<void>;
  headerActions?: React.ReactNode;
}

function PDFSidebar({
  fileId,
  filePath,
  bennyLastAttemptedAt,
  bennyRetrievalStatus,
  bennyRetrievalReason,
  onUploadPdf,
  onRemovePdf,
  headerActions,
}: PDFSidebarProps) {
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const [pdfError, setPdfError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfWidth, setPdfWidth] = useState(() => calculatePdfWidth(theme));
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentList = useMemo<DocumentList | null>(() => {
    if (!fileId || !filePath) return null;
    return [["Benny", [{ fileId, path: filePath }]]];
  }, [fileId, filePath]);

  const pdfUrls = useMemo(() => {
    if (!documentList) return [];
    return toSourceUrls(documentList);
  }, [documentList]);

  const retrievalText = useMemo(() => {
    if (bennyRetrievalStatus === "SUCCESS") {
      return bennyLastAttemptedAt
        ? `Retrieved by Benny on ${formatDateTime(bennyLastAttemptedAt)}`
        : "Retrieved by Benny";
    }
    return "Manually uploaded";
  }, [bennyRetrievalStatus, bennyLastAttemptedAt]);

  useEffect(() => {
    setPdfWidth(calculatePdfWidth(theme));
  }, [theme]);

  const handlePdfError = () => {
    setPdfError(true);
    setIsLoading(false);
  };

  const handlePdfLoad = () => {
    setPdfError(false);
    setIsLoading(false);
  };

  const handleDownload = async () => {
    if (!pdfUrls.length) return;

    try {
      const fetchOptions: RequestInit = {};
      if (USE_AUTH) {
        const accessToken = await getAccessTokenSilently();
        fetchOptions.headers = {
          Authorization: `Bearer ${accessToken}`,
        };
      }
      const response = await fetch(pdfUrls[0], fetchOptions);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `verification-${fileId || "document"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error("Failed to download PDF:", error);
    }
  };

  const handleFullscreen = () => {
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  const hasValidPdf = fileId && filePath && pdfUrls.length > 0;

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!onUploadPdf) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      await onUploadPdf(file);
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : "Failed to upload PDF",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    if (selectedFile.type !== "application/pdf") {
      setUploadError("Please select a PDF file");
      return;
    }

    setUploadError(null);

    if (hasValidPdf) {
      setPendingFile(selectedFile);
      setShowConfirmModal(true);
    } else {
      handleFileUpload(selectedFile);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleConfirmReplace = () => {
    setShowConfirmModal(false);
    if (pendingFile) {
      handleFileUpload(pendingFile);
      setPendingFile(null);
    }
  };

  const handleCancelReplace = () => {
    setShowConfirmModal(false);
    setPendingFile(null);
  };

  const handleRemoveClick = () => {
    setShowRemoveConfirmModal(true);
  };

  const handleCancelRemove = () => {
    setShowRemoveConfirmModal(false);
  };

  const handleConfirmRemove = async () => {
    if (!onRemovePdf) return;

    setIsRemoving(true);
    try {
      await onRemovePdf();
    } catch {
      // Error surfaces via snackbar from the caller.
    } finally {
      setIsRemoving(false);
      setShowRemoveConfirmModal(false);
    }
  };

  const renderNoPdfState = () => (
    <Box
      sx={{
        p: 3,
        pt: 2,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        height: "100%",
        color: "text.secondary",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 56,
          backgroundColor: "grey.100",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <PictureAsPdf sx={{ fontSize: 24, color: "grey.400" }} />
      </Box>

      <WarningAmber
        sx={{
          fontSize: 20,
          color: "warning.main",
          mb: 2,
        }}
      />

      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          mb: 2,
          color: "text.primary",
          fontSize: "1.1rem",
        }}
      >
        No PDF Available
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mb: 3,
          lineHeight: 1.5,
          maxWidth: 280,
          color: "text.secondary",
        }}
      >
        Benny could not retrieve the verification PDF for this appointment.
      </Typography>

      <Box
        sx={{
          p: 1.5,
          backgroundColor: "grey.100",
          borderRadius: 1,
          width: "100%",
          border: "1px solid",
          borderColor: "grey.300",
          mb: 3,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            lineHeight: 1.4,
            fontSize: "0.875rem",
            fontWeight: 500,
          }}
        >
          Manual verification required. Contact the insurance carrier directly
          to verify coverage.
        </Typography>
      </Box>

      {onUploadPdf && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadFile />}
          onClick={handleFileInputClick}
          disabled={isUploading}
          sx={{ minWidth: 200 }}
        >
          {isUploading ? "Uploading..." : "Upload PDF"}
        </Button>
      )}

      {uploadError && (
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: "error.main",
            fontSize: "0.875rem",
          }}
        >
          {uploadError}
        </Typography>
      )}
    </Box>
  );

  const renderPdfError = () => (
    <Box
      sx={{
        p: 3,
        pt: 2,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        height: "100%",
      }}
    >
      <WarningAmber
        sx={{
          fontSize: 20,
          color: "error.main",
          mb: 2,
        }}
      />
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          mb: 2,
          color: "text.primary",
          fontSize: "1.1rem",
        }}
      >
        PDF Access Error
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          lineHeight: 1.5,
          maxWidth: 280,
          color: "text.secondary",
        }}
      >
        Unable to access the PDF file. This may be due to insufficient
        permissions or the file being temporarily unavailable.
      </Typography>
    </Box>
  );

  const renderPdfViewer = () => (
    <Box sx={{ p: 1, height: "100%" }}>
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          borderRadius: 1,
          backgroundColor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        {pdfUrls.map((url) => (
          <Source
            key={url}
            url={url}
            pageWidth={pdfWidth}
            onLoadSuccess={handlePdfLoad}
            onLoadError={handlePdfError}
            maxHeight="none"
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        overflow: "hidden",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 1,
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PictureAsPdf
            sx={{
              fontSize: 16,
              color: "text.secondary",
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "text.primary",
              letterSpacing: "0.01em",
            }}
          >
            Verification PDF
          </Typography>
        </Box>

        {hasValidPdf && !pdfError && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {headerActions}
            <Tooltip title="Download PDF">
              <IconButton
                size="small"
                onClick={handleDownload}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            {onUploadPdf && (
              <Tooltip title="Replace PDF">
                <IconButton
                  size="small"
                  onClick={handleFileInputClick}
                  disabled={isUploading}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <UploadFile fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onRemovePdf && (
              <Tooltip title="Remove PDF">
                <IconButton
                  size="small"
                  onClick={handleRemoveClick}
                  disabled={isRemoving}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "error.main" },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View fullscreen">
              <IconButton
                size="small"
                onClick={handleFullscreen}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <Fullscreen fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: "auto", position: "relative", pt: 1 }}>
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {(() => {
          if (!hasValidPdf) return renderNoPdfState();
          if (pdfError) return renderPdfError();
          return renderPdfViewer();
        })()}
      </Box>

      {hasValidPdf && !pdfError && (
        <>
          <Divider />
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "grey.50",
              borderBottomLeftRadius: 2,
              borderBottomRightRadius: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "text.secondary",
                fontSize: "0.8rem",
                fontWeight: 500,
              }}
            >
              {retrievalText}
              {bennyRetrievalReason && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", ml: 1 }}
                >
                  {bennyRetrievalReason}
                </Typography>
              )}
            </Typography>
          </Box>
        </>
      )}

      {/* Fullscreen PDF Modal */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "95vh",
            maxWidth: "95vw",
            maxHeight: "95vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PictureAsPdf sx={{ fontSize: 20, color: "text.secondary" }} />
            <Typography variant="h6">Verification PDF - Full View</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownload} size="small">
                <Download />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCloseFullscreen} size="small">
              <Typography variant="body2">✕</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{ p: 0, overflow: "hidden", flex: 1, display: "flex" }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflow: "auto",
              backgroundColor: "grey.50",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {pdfUrls.map((url) => (
              <Source
                key={`fullscreen-${url}`}
                url={url}
                pageWidth={Math.min(window.innerWidth * 0.8, 800)}
                onLoadSuccess={handlePdfLoad}
                onLoadError={handlePdfError}
                maxHeight="100vh"
              />
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Confirmation Modal for Replacing Existing PDF */}
      <Dialog
        open={showConfirmModal}
        onClose={handleCancelReplace}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Replace Existing PDF?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will replace the existing attached PDF with your uploaded file.
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReplace} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReplace}
            variant="contained"
            color="primary"
            autoFocus
          >
            Replace PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal for Removing PDF */}
      <Dialog
        open={showRemoveConfirmModal}
        onClose={handleCancelRemove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove PDF?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the uploaded PDF from this verification record.
            This action cannot be undone from this screen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelRemove}
            color="inherit"
            disabled={isRemoving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            color="error"
            disabled={isRemoving}
            autoFocus
          >
            {isRemoving ? "Removing..." : "Remove PDF"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PDFSidebar;
